import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  ShoppingCart, Search, Plus, Minus, Trash2, ChevronLeft, CheckCircle2,
  Package, CalendarClock, RotateCcw, Save, Repeat, X, Clock, AlertCircle,
  UserCog, HelpCircle, Send,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";

/*
 * /portal/order-new — full-page "online store" for clients to place orders.
 *
 * Features:
 *  - Site selector → lists that site's equipment as a catalogue grid.
 *  - Live search filter.
 *  - Floating cart (always visible) with qty +/-, notes, and removal.
 *  - "Re-order last" button — one-tap hydrates cart from most recent order.
 *  - "Save as draft" — persists to `localStorage` per customer so the client
 *    can come back later (portal user session-scoped).
 *  - "Make recurring" — sets `recurring_interval_days` + `recurring_starts_on`
 *    so the CRM can queue repeat orders automatically.
 */

const DRAFT_KEY = (cid) => `hv:portal-order-draft:${cid}`;

export default function PortalOrderNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const BRAND_NAME = branding?.app_name || "Rogue";
  const [searchParams] = useSearchParams();

  // Resolve customer_id with belt-and-braces:
  //   - real client     → their own user.customer_id (always trustworthy)
  //   - staff preview   → `?customer_id=...&as_staff=1` from the preview link
  //   - impersonating-but-staff-token-leaked (the production bug fixed in
  //     Iter 290zp.13z6) → still works because we always pass
  //     `?customer_id=` from the /portal "Place an order" button now.
  //   [Iter 290zp.13z8] Falling back to `searchParams` for client sessions
  //   too means a portal link shared with the customer (with the cid in
  //   the URL) still works if their JWT happens to be missing the field.
  const isClient = user?.role === "client";
  const isStaffPlacingOnBehalf =
    !isClient && !!searchParams.get("customer_id") && searchParams.get("as_staff") === "1";
  const customerId =
    (isClient && user?.customer_id) ||
    searchParams.get("customer_id") ||
    user?.customer_id ||
    null;
  const [customerMeta, setCustomerMeta] = useState(null);

  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [equipment, setEquipment] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [neededBy, setNeededBy] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [customRequest, setCustomRequest] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [recurInterval, setRecurInterval] = useState(30);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [draftRestored, setDraftRestored] = useState(false);

  // ── Bootstrap: sites + recent orders + restore draft ─────────────
  useEffect(() => {
    if (!customerId) return;
    (async () => {
      setLoading(true);
      try {
        // Staff placing on behalf: use CRM endpoints with customer_id filter;
        // clients: use portal endpoints. [Iter 290zp.13z8] Always pass
        // customer_id as the qs so /portal/sites works even if user.customer_id
        // is missing on the JWT (production token-swap leak fallback).
        const sitesUrl = isClient
          ? `/portal/sites?customer_id=${customerId}`
          : `/sites?customer_id=${customerId}`;
        const ordersUrl = isClient
          ? "/order-requests?customer_id=" + customerId
          : "/order-requests?customer_id=" + customerId;
        const promises = [api.get(sitesUrl), api.get(ordersUrl)];
        if (isStaffPlacingOnBehalf) {
          promises.push(api.get(`/customers/${customerId}`));
        }
        const [s, o, c] = await Promise.all(promises);
        setSites(s.data || []);
        setHistory(o.data || []);
        if (c) setCustomerMeta(c.data);
        if ((s.data || []).length === 1) setSiteId(s.data[0].id);
        // Restore draft if present
        try {
          const raw = localStorage.getItem(DRAFT_KEY(customerId));
          if (raw) {
            const d = JSON.parse(raw);
            if (d?.cart?.length) {
              setCart(d.cart);
              if (d.siteId) setSiteId(d.siteId);
              if (d.neededBy) setNeededBy(d.neededBy);
              if (d.orderNotes) setOrderNotes(d.orderNotes);
              if (d.recurring) { setRecurring(true); setRecurInterval(d.recurInterval || 30); }
              setDraftRestored(true);
            }
          }
        } catch {}
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId, isClient, isStaffPlacingOnBehalf]);

  const [showAllRegistry, setShowAllRegistry] = useState(false);

  // Load equipment when site changes (or full registry when toggle is on)
  useEffect(() => {
    if (showAllRegistry) {
      (async () => {
        try {
          // Whole equipment registry (catalogue master) — reps + clients can
          // type in things they need that aren't on their site yet.
          const e = await api.get(`/inventory`);
          setEquipment(e.data || []);
        } catch (e2) {
          setErr(formatErr(e2?.response?.data?.detail, "Could not load registry"));
        }
      })();
      return;
    }
    if (!siteId) { setEquipment([]); return; }
    (async () => {
      try {
        const e = await api.get(`/equipment?site_id=${siteId}`);
        setEquipment(e.data || []);
      } catch (e2) {
        setErr(formatErr(e2?.response?.data?.detail, "Could not load equipment"));
      }
    })();
  }, [siteId, showAllRegistry]);

  // Iter 398 — support deep-link from EquipmentBlock's Reorder button:
  // ?site_id=… auto-selects the site, ?prefill_equipment_id=… adds one
  // unit of that equipment to the cart as soon as it's loaded.
  useEffect(() => {
    const siteParam = searchParams.get("site_id");
    if (siteParam && !siteId) setSiteId(siteParam);
  }, [searchParams, siteId]);
  useEffect(() => {
    const eqId = searchParams.get("prefill_equipment_id");
    if (!eqId || !equipment.length) return;
    const match = equipment.find((eq) => eq.id === eqId);
    if (!match) return;
    setCart((prev) => {
      if (prev.some((l) => l.equipment_id === eqId)) return prev;
      return [...prev, {
        key: `${eqId}-prefill-${Date.now()}`,
        equipment_id: eqId,
        name: match.name,
        sku: match.sku || "",
        unit: match.unit || "pcs",
        quantity: 1,
        notes: "",
      }];
    });
  }, [equipment, searchParams]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return equipment;
    return equipment.filter(
      (eq) =>
        (eq.name || "").toLowerCase().includes(q) ||
        (eq.sku || "").toLowerCase().includes(q) ||
        (eq.category || "").toLowerCase().includes(q),
    );
  }, [equipment, search]);

  // Group filtered items by category for "Sort by type" UX.
  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((eq) => {
      const cat = (eq.category || "general").toString();
      if (!out[cat]) out[cat] = [];
      out[cat].push(eq);
    });
    return Object.entries(out).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const addToCart = (eq) => {
    setCart((prev) => {
      const found = prev.find((l) => l.equipment_id === eq.id);
      if (found) return prev.map((l) => l.equipment_id === eq.id ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, {
        key: `${eq.id}-${Date.now()}`,
        equipment_id: eq.id,
        name: eq.name,
        sku: eq.sku || "",
        unit: eq.unit || "pcs",
        quantity: 1,
        notes: "",
      }];
    });
  };

  const updateLine = (key, patch) => setCart((p) => p.map((l) => l.key === key ? { ...l, ...patch } : l));
  const removeLine = (key) => setCart((p) => p.filter((l) => l.key !== key));

  const reorderLast = () => {
    const last = history[0];
    if (!last) { setErr("No previous orders to copy."); return; }
    if (last.site_id) setSiteId(last.site_id);
    const lines = (last.lines || []).map((l, i) => ({
      key: `reord-${i}-${Date.now()}`,
      equipment_id: l.site_equipment_id || l.equipment_id || "",
      name: l.name,
      sku: l.sku || "",
      unit: l.unit || "pcs",
      quantity: l.quantity || 1,
      notes: l.notes || "",
    }));
    setCart(lines);
    setSuccess(`${lines.length} item${lines.length === 1 ? "" : "s"} restored from your last order.`);
    setErr("");
  };

  const saveDraft = () => {
    if (!customerId) return;
    try {
      localStorage.setItem(DRAFT_KEY(customerId), JSON.stringify({
        cart, siteId, neededBy, orderNotes, recurring, recurInterval,
        savedAt: new Date().toISOString(),
      }));
      setSuccess("Draft saved — you can return and pick up where you left off.");
      setErr("");
    } catch {
      setErr("Could not save draft.");
    }
  };

  const clearDraft = () => {
    if (!customerId) return;
    try { localStorage.removeItem(DRAFT_KEY(customerId)); } catch {}
    setDraftRestored(false);
  };

  const submit = async () => {
    setErr(""); setSuccess("");
    if (cart.length === 0) { setErr("Add at least one item."); return; }
    setBusy(true);
    try {
      const body = {
        customer_id: customerId,
        site_id: siteId || undefined,
        preferred_date: neededBy || undefined,
        notes: orderNotes || "",
        lines: cart.map(({ key: _k, ...line }) => line),
      };
      if (customRequest.trim()) {
        body.notes = `${body.notes ? body.notes + " · " : ""}[Custom request] ${customRequest.trim()}`.trim();
      }
      if (recurring) {
        body.notes = `[Recurring every ${recurInterval} days] ${body.notes || ""}`.trim();
      }
      if (isStaffPlacingOnBehalf) {
        body.notes = `[Placed by ${user?.name || user?.email} on behalf] ${body.notes || ""}`.trim();
      }
      await api.post("/order-requests", body);
      clearDraft();
      if (isStaffPlacingOnBehalf) {
        navigate(`/portal/preview/${customerId}?order=submitted`, { replace: true });
      } else {
        navigate("/portal?order=submitted", { replace: true });
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not submit order"));
    } finally {
      setBusy(false);
    }
  };

  const cartCount = cart.reduce((n, l) => n + (Number(l.quantity) || 0), 0);

  return (
    <div
      className="min-h-screen relative"
      style={{
        background:
          "radial-gradient(ellipse at top left, rgba(167,139,250,0.08) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(31,182,193,0.08) 0%, transparent 55%), var(--hv-bg)",
        color: "var(--hv-text)",
      }}
      data-testid="portal-order-new"
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header
        className="sticky top-0 z-20 backdrop-blur-xl hv-safe-top"
        style={{
          borderBottom: "1px solid var(--hv-border)",
          background:
            "color-mix(in srgb, var(--hv-bg) 85%, transparent)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Link
            to={isStaffPlacingOnBehalf ? `/portal/preview/${customerId}` : "/portal"}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="order-new-back"
          ><ChevronLeft className="w-4 h-4" /> Back</Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e]">
              <ShoppingCart className="w-4 h-4" strokeWidth={2.5} />
            </div>
            <div>
              <h1 className="font-display text-base md:text-xl font-bold leading-tight">
                Place an order
                {isStaffPlacingOnBehalf && customerMeta && (
                  <span className="ml-2 text-xs font-normal text-[#1FB6C1]">
                    for {customerMeta.name}
                  </span>
                )}
              </h1>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--hv-text-muted)] flex items-center gap-1.5">
                {BRAND_NAME} storefront
                {isStaffPlacingOnBehalf && (
                  <span className="inline-flex items-center gap-1 text-[#A78BFA]">
                    <UserCog className="w-3 h-3" /> Staff on-behalf mode
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex-1" />
          {history[0] && (
            <button onClick={reorderLast} className="hv-btn hv-btn-ghost text-xs" data-testid="order-new-reorder-last">
              <RotateCcw className="w-3 h-3" /> Re-order last
            </button>
          )}
          <button onClick={saveDraft} className="hv-btn hv-btn-ghost text-xs" data-testid="order-new-save-draft">
            <Save className="w-3 h-3" /> Save draft
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 pb-[calc(14rem+env(safe-area-inset-bottom))] space-y-5">
        {draftRestored && (
          <div className="hv-card p-3 flex items-center gap-3 border border-[#1FB6C1]/30 bg-[#1FB6C1]/5">
            <Clock className="w-4 h-4 text-[#1FB6C1] shrink-0" />
            <div className="text-xs text-[var(--hv-text-secondary)] flex-1">We've restored your saved draft so you can pick up where you left off.</div>
            <button onClick={() => { setCart([]); setNeededBy(""); setOrderNotes(""); setRecurring(false); clearDraft(); }} className="hv-btn hv-btn-ghost text-[10px]">
              <X className="w-3 h-3" /> Start fresh
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-[var(--hv-text-muted)]">Loading store…</div>
        ) : sites.length === 0 ? (
          <div className="hv-card p-6 text-center space-y-2">
            <Package className="w-8 h-8 mx-auto text-[var(--hv-text-muted)]" />
            <div className="text-sm text-[var(--hv-text-secondary)]">No sites on your account yet.</div>
            <div className="text-xs text-[var(--hv-text-muted)]">Please contact your account manager to set up at least one site so we can match orders to your location.</div>
          </div>
        ) : (
          <>
            {/* Site + Filters row */}
            <section className="hv-card p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--hv-text-muted)]">Site</label>
                  <select
                    value={siteId}
                    onChange={(e) => setSiteId(e.target.value)}
                    className="hv-input mt-1 text-sm"
                    disabled={showAllRegistry}
                    data-testid="order-new-site-select"
                  >
                    <option value="">— Select a site —</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--hv-text-muted)]">Search equipment</label>
                  <div className="relative mt-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--hv-text-muted)]" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Name, SKU, type, part number…"
                      className="hv-input text-sm !pl-9"
                      disabled={!siteId && !showAllRegistry}
                      data-testid="order-new-search"
                    />
                  </div>
                </div>
              </div>
              <label
                className="flex items-center gap-2 text-xs text-[var(--hv-text-secondary)] cursor-pointer select-none"
                data-testid="order-new-show-all-toggle"
              >
                <input
                  type="checkbox"
                  checked={showAllRegistry}
                  onChange={(e) => setShowAllRegistry(e.target.checked)}
                  className="accent-[#1FB6C1]"
                />
                Show full {BRAND_NAME} registry (add equipment that isn't on this
                site yet)
              </label>
            </section>

            {/* Catalogue grid — grouped by category */}
            <section data-testid="order-new-catalogue">
              {!siteId && !showAllRegistry ? (
                <div className="hv-card p-8 text-center text-xs text-[var(--hv-text-muted)]">
                  Pick a site above to browse what's installed there, or tick
                  "Show full registry" to search the master catalogue.
                </div>
              ) : filtered.length === 0 ? (
                <div className="hv-card p-8 text-center text-xs text-[var(--hv-text-muted)]">
                  {equipment.length === 0 ? "No equipment registered." : "No results for that search."}
                </div>
              ) : (
                <div className="space-y-5">
                  {grouped.map(([cat, items]) => (
                    <div key={cat} data-testid={`order-new-cat-${cat}`}>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[#1FB6C1] font-bold mb-2 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1FB6C1]" />
                        {cat}
                        <span className="text-[var(--hv-text-muted)] font-normal">· {items.length}</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((eq) => {
                    const inCart = cart.find((l) => l.equipment_id === eq.id);
                    return (
                      <div
                        key={eq.id}
                        className={`hv-card p-4 flex flex-col gap-2 transition group hover:border-[#1FB6C1]/50 hover:shadow-[0_0_24px_-8px_rgba(31,182,193,0.35)] ${inCart ? "border-[#1FB6C1]/60 bg-[#1FB6C1]/[0.03]" : ""}`}
                        data-testid={`order-new-product-${eq.id}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#1FB6C1]/20 to-[#A78BFA]/20 flex items-center justify-center border border-[var(--hv-border)] shrink-0">
                            <Package className="w-5 h-5 text-[#1FB6C1]" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm text-[var(--hv-text)] truncate">{eq.name}</div>
                            <div className="text-[10px] font-mono text-[var(--hv-text-muted)] truncate">
                              {eq.sku ? `SKU ${eq.sku}` : "No SKU"} · {eq.unit || "pcs"}
                            </div>
                          </div>
                        </div>
                        {eq.description && <div className="text-[11px] text-[var(--hv-text-secondary)] line-clamp-2">{eq.description}</div>}
                        <div className="mt-auto pt-2 flex items-center gap-2">
                          {inCart ? (
                            <div className="flex items-center gap-1 flex-1">
                              <button onClick={() => updateLine(inCart.key, { quantity: Math.max(0, inCart.quantity - 1) })} className="w-8 h-8 rounded border border-[var(--hv-border)] hover:bg-white/5 flex items-center justify-center">
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                min={0}
                                value={inCart.quantity}
                                onChange={(e) => updateLine(inCart.key, { quantity: Math.max(0, Number(e.target.value) || 0) })}
                                className="hv-input text-center text-sm font-mono !h-8 flex-1"
                                data-testid={`order-new-qty-${eq.id}`}
                              />
                              <button onClick={() => updateLine(inCart.key, { quantity: inCart.quantity + 1 })} className="w-8 h-8 rounded border border-[var(--hv-border)] hover:bg-white/5 flex items-center justify-center">
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => addToCart(eq)}
                              data-testid={`order-new-add-${eq.id}`}
                              className="hv-btn hv-btn-ghost text-xs flex-1 justify-center"
                              style={{ borderColor: "#1FB6C1", color: "#1FB6C1" }}
                            >
                              <Plus className="w-3 h-3" /> Add to cart
                            </button>
                          )}
                        </div>
                      </div>
                    );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* "Couldn't find it?" — free-text custom request */}
            <section className="hv-card p-4 md:p-5 border border-[#1FB6C1]/25 bg-gradient-to-br from-[#1FB6C1]/[0.04] to-transparent" data-testid="order-new-custom-request">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 flex items-center justify-center shrink-0">
                  <HelpCircle className="w-4 h-4 text-[#1FB6C1]" />
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <h3 className="font-display text-base font-bold">Couldn't find what you were looking for?</h3>
                    <p className="text-xs text-[var(--hv-text-secondary)] leading-relaxed">
                      Tell us what you need — part name, brand, size, photo URL, anything.
                      We'll source it and get back to you with a quote.
                    </p>
                  </div>
                  <VoiceTextarea
                    value={customRequest}
                    onChange={setCustomRequest}
                    placeholder="e.g. Replacement seal kit for 10kW Grundfos pump (model XYZ), need 2 of them, urgent for site visit Friday."
                    rows={3}
                    data-testid="order-new-custom-request-input"
                   testIdPrefix="order-new-custom-request-input-voice" context="note" />
                  <div className="text-[10px] text-[var(--hv-text-muted)]">
                    {customRequest.trim().length > 0
                      ? "Will be attached to your order when you submit, or you can send it on its own."
                      : "Optional. Adds extra context to your order."}
                  </div>
                </div>
                {customRequest.trim().length > 0 && cart.length === 0 && (
                  <button
                    type="button"
                    onClick={async () => {
                      setErr(""); setSuccess(""); setBusy(true);
                      try {
                        await api.post("/order-requests", {
                          customer_id: customerId,
                          site_id: siteId || undefined,
                          preferred_date: neededBy || undefined,
                          notes: `[Custom request] ${customRequest.trim()}`,
                          lines: [],
                        });
                        setCustomRequest("");
                        setSuccess("Request sent — we'll be in touch.");
                      } catch (e) {
                        setErr(formatErr(e?.response?.data?.detail, "Could not send request"));
                      } finally { setBusy(false); }
                    }}
                    disabled={busy}
                    className="hv-btn hv-btn-ghost text-xs shrink-0 self-start"
                    style={{ borderColor: "#1FB6C1", color: "#1FB6C1" }}
                    data-testid="order-new-custom-request-send"
                  >
                    <Send className="w-3 h-3" /> Send request
                  </button>
                )}
              </div>
            </section>
            {history.length > 0 && (
              <section className="hv-card p-4 space-y-3" data-testid="order-new-history">
                <div className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-[#A78BFA]" />
                  <h3 className="font-display text-base font-bold">Recent orders</h3>
                  <span className="text-[10px] font-mono text-[var(--hv-text-muted)]">({history.length})</span>
                </div>
                <div className="divide-y divide-white/5">
                  {history.slice(0, 5).map((o) => (
                    <div key={o.id} className="py-2 flex items-center gap-3 text-xs">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {o.lines?.length || 0} item{o.lines?.length === 1 ? "" : "s"}
                          <span className="text-[var(--hv-text-muted)] ml-2">· {o.status}</span>
                        </div>
                        <div className="text-[10px] text-[var(--hv-text-muted)] truncate">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}
                          {o.notes ? ` · ${o.notes.slice(0, 60)}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const lines = (o.lines || []).map((l, i) => ({
                            key: `rh-${o.id}-${i}-${Date.now()}`,
                            equipment_id: l.site_equipment_id || l.equipment_id || "",
                            name: l.name,
                            sku: l.sku || "",
                            unit: l.unit || "pcs",
                            quantity: l.quantity || 1,
                            notes: l.notes || "",
                          }));
                          setCart(lines);
                          if (o.site_id) setSiteId(o.site_id);
                          setSuccess("Items restored from this order.");
                        }}
                        className="hv-btn hv-btn-ghost text-[10px]"
                        data-testid={`order-new-reuse-${o.id}`}
                      ><RotateCcw className="w-3 h-3" /> Reuse</button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* ── Sticky cart summary ───────────────────────────────────── */}
      {cart.length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#1FB6C1]/30 backdrop-blur-xl pb-[env(safe-area-inset-bottom)] bg-[var(--hv-surface)]/95 text-[var(--hv-text)]"
          data-testid="order-new-cart-bar"
        >
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1]">
                <ShoppingCart className="w-4 h-4" />
              </div>
              <div className="text-sm font-semibold">Your cart</div>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#1FB6C1]/40 text-[#1FB6C1] bg-[#1FB6C1]/10" data-testid="order-new-cart-count">
                {cart.length} line{cart.length === 1 ? "" : "s"} · {cartCount} item{cartCount === 1 ? "" : "s"}
              </span>
              <div className="flex-1" />
              <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--hv-text-muted)] hidden sm:inline">Needed by</label>
              <DatePickerInput
                value={neededBy}
                onChange={setNeededBy}
                className="w-[150px]"
                testId="order-new-needed-by"
                minDate={new Date()}
              />
              <button
                onClick={() => setRecurring((v) => !v)}
                className="hv-btn hv-btn-ghost text-xs"
                style={recurring ? { borderColor: "#A78BFA", color: "#A78BFA" } : undefined}
                data-testid="order-new-recurring-toggle"
                title="Repeat this order automatically"
              ><Repeat className="w-3 h-3" /> {recurring ? `Every ${recurInterval}d` : "Make recurring"}</button>
              {recurring && (
                <select
                  value={recurInterval}
                  onChange={(e) => setRecurInterval(Number(e.target.value))}
                  className="hv-input text-xs !h-8 w-[110px]"
                  data-testid="order-new-recurring-interval"
                >
                  <option value={7}>Every 7 days</option>
                  <option value={14}>Every 14 days</option>
                  <option value={30}>Every 30 days</option>
                  <option value={60}>Every 60 days</option>
                  <option value={90}>Every 90 days</option>
                </select>
              )}
            </div>

            <div className="max-h-40 overflow-y-auto divide-y divide-white/5 -mx-1 px-1" data-testid="order-new-cart-lines">
              {cart.map((line) => (
                <div key={line.key} className="py-1.5 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{line.name}</div>
                    <div className="text-[10px] text-[var(--hv-text-muted)] font-mono truncate">{line.sku || "No SKU"} · {line.unit}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateLine(line.key, { quantity: Math.max(0, line.quantity - 1) })} className="w-6 h-6 rounded border border-[var(--hv-border)] hover:bg-white/5 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <input
                      type="number"
                      min={0}
                      value={line.quantity}
                      onChange={(e) => updateLine(line.key, { quantity: Number(e.target.value) })}
                      className="hv-input text-xs !h-7 w-14 text-center"
                    />
                    <button onClick={() => updateLine(line.key, { quantity: line.quantity + 1 })} className="w-6 h-6 rounded border border-[var(--hv-border)] hover:bg-white/5 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => removeLine(line.key)} className="w-7 h-7 rounded text-rose-300 hover:bg-rose-500/10 flex items-center justify-center" data-testid={`order-new-remove-${line.key}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>

            <VoiceTextarea
              value={orderNotes}
              onChange={setOrderNotes}
              placeholder="Delivery instructions, site contact, urgency notes…"
              rows={2}
              data-testid="order-new-notes"
             testIdPrefix="order-new-notes-voice" context="note" />

            {err && <div className="text-xs text-rose-300 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {err}</div>}
            {success && <div className="text-xs text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {success}</div>}

            <div className="flex items-center gap-2">
              <button
                onClick={() => { setCart([]); clearDraft(); }}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="order-new-clear-cart"
              ><Trash2 className="w-3 h-3" /> Clear cart</button>
              <div className="flex-1" />
              <button
                onClick={submit}
                disabled={busy || cart.length === 0}
                className="hv-btn hv-btn-ghost text-xs"
                style={{ borderColor: "#1FB6C1", color: "#1FB6C1" }}
                data-testid="order-new-submit"
              >
                <CheckCircle2 className="w-3 h-3" /> {busy ? "Submitting…" : recurring ? "Place recurring order" : "Place order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
